/**
 * ServiceSummaryStep — Step 2 of the service checkout variant.
 *
 * Renders:
 *   - Reservation summary card (product title + seller + date + time
 *     range + guests + deposit amount).
 *   - Payment breakdown card (deposit % now + balance % on completion +
 *     72h seller-confirmation window + 100% refund on seller reject).
 *   - Escrow explanation paragraph (24h auto-release after delivery).
 *   - Inline error alert + footer (Atrás → Step 1 / Continuar → Step 3).
 *
 * Founder directive (2026-11): 2FA removed from checkout — no
 * marketplace requires 2FA to buy. The "Verificar 2FA" button is now a
 * "Continuar" button that transitions directly to the verify step.
 * The TwoFaActivationRequiredCard (2FA enrollment gate) was also
 * removed.
 *
 * SPEC-244 · partición VERBATIM de
 * frontend/src/components/market/checkout-modal.tsx (2.637 L).
 *
 * Preserved VERBATIM — the original Step 2 JSX block (lines 2145-2238 of
 * the monolith).
 *
 * Anti-P-041: 0 `as any`. Anti-P-042: 0 `console.*`.
 * Anti-P-366: only adds the 2FA pre-check (Bug #2 fix).
 */

import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { ArrowLeft, ArrowRight, Clock } from 'lucide-react';
import type { PublicProductResponse } from '@profileia/shared';
import { formatCredits } from '../helpers';
import { SubmitErrorAlert } from './submit-error-alert';

// ─── Component ───────────────────────────────────────────────────────────────

export interface ServiceSummaryStepProps {
  product: PublicProductResponse;
  reservationDate: string;
  reservationTimeStart: string;
  reservationTimeEnd: string;
  guestCount: number;
  depositPercent: number;
  depositAmount: number;
  submitting: boolean;
  submitError: string | null;
  onBack: () => void;
  /** Transitions to the verify step (Founder directive 2026-11: 2FA
   * removed — this used to open the 2FA dialog, now it just advances). */
  onContinue: () => void;
  onClose: () => void;
}

export function ServiceSummaryStep({
  product,
  reservationDate,
  reservationTimeStart,
  reservationTimeEnd,
  guestCount,
  depositPercent,
  depositAmount,
  submitting,
  submitError,
  onBack,
  onContinue,
  onClose,
}: ServiceSummaryStepProps) {
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-muted/40 p-4 space-y-2">
        <p className="text-sm font-medium line-clamp-2">{product.title}</p>
        <p className="text-xs text-muted-foreground">
          Vendido por <span className="font-medium">{product.seller.storeName}</span>
        </p>
        <dl className="space-y-1.5 text-sm mt-2">
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Fecha de reserva</dt>
            <dd className="font-medium">{reservationDate}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Rango horario</dt>
            <dd className="font-medium tabular-nums">
              {reservationTimeStart} – {reservationTimeEnd}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Invitados</dt>
            <dd className="font-medium tabular-nums">{guestCount}</dd>
          </div>
          <div className="border-t border-border pt-2 flex justify-between text-base">
            <dt className="font-semibold">Depósito ({depositPercent}%)</dt>
            <dd className="font-bold tabular-nums text-primary">
              {formatCredits(depositAmount)} créditos
            </dd>
          </div>
        </dl>
      </div>

      <div className="rounded-md border border-border p-3 text-sm">
        <p className="font-medium flex items-center gap-1.5 mb-1">
          <Clock className="size-4" aria-hidden="true" />
          Desglose de pago
        </p>
        <p className="text-xs text-muted-foreground">
          Depósito {depositPercent}% ahora + {100 - depositPercent}% al completar el servicio.
          El vendedor tiene 72h para confirmar disponibilidad. Si rechaza, se te
          reembolsa el 100% del depósito automáticamente.
        </p>
      </div>

      <p className="text-xs text-muted-foreground">
        Al confirmar, el depósito se debitará a una cuenta de
        <strong> escrow</strong>. El balance se cargará automáticamente al
        confirmar recepción del servicio (o se liberará solo el depósito
        si no confirmas en 24h tras la entrega).
      </p>

      {submitError && <SubmitErrorAlert submitError={submitError} />}

      <DialogFooter className="sticky bottom-0 -mx-6 -mb-6 mt-2 flex-col gap-2 border-t border-border bg-background px-6 py-3 sm:flex-col">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className="flex-1"
          disabled={submitting}
        >
          <ArrowLeft className="size-4 mr-1" aria-hidden="true" />
          Atrás
        </Button>
        <Button
          type="button"
          className="flex-1"
          onClick={onContinue}
          disabled={submitting}
        >
          Continuar
          <ArrowRight className="size-4 ml-1" aria-hidden="true" />
        </Button>
      </DialogFooter>
    </div>
  );
}
